import { trackConfig, type IFramework } from '@/framework';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import {
    LayerType,
    MaterialIdsType,
    TextAlignType,
    type ILayerProps,
    type IListStyleMaterial,
    type ITextLayerProps,
    type ListStyleStruct,
} from '@/version-types';
import {
    applySnapshotSize,
    collectFontMaterialIDs,
    collectFontSubsetRequests,
    pushHistory,
    refreshRuntimeFontFamilies,
    requestTextSVGPreview,
    updateLayer,
} from '../utils';
import { getTextLayerHost } from '../utils/host';

export interface IListText {
    /**
     * 应用列表文本
     *
     * @param layerID - 图层 ID
     * @param data - 列表样式配置
     * @returns
     */
    applyListText(layerID: string, data: ListStyleStruct): Promise<ILayerProps | void>;

    /**
     * 取消设置列表文本
     *
     * @param layerID - 图层 ID
     * @returns
     */
    cancelListText(layerID: string): Promise<ILayerProps | void>;

    /**
     * 更新列表素材配置
     *
     * @param layerID - 图层 ID
     * @param data - 列表素材配置
     * @param options - 选项
     * @returns
     */
    updateListStyleMaterial(
        layerID: string,
        data: IListStyleMaterial,
        options?: { writeHistory?: boolean },
    ): ILayerProps | void;
}

const FUNC_ID = trackConfig[LayerType.TEXT_LAYER].listStyle;

export class ListText implements IListText {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    /**
     * 准备列表样式候选，并等待真实 Text Layer 宿主处理字体依赖后尝试排版。
     *
     * @param prevLayer - 修改前的 Text Layer。
     * @param data - 目标列表样式；null 表示取消列表。
     * @returns ready 布局已应用的候选图层；输入、字体或 Renderer 不可用时返回 null。
     */
    private async prepare(prevLayer: ILayerProps, data: ListStyleStruct | null): Promise<ILayerProps | null> {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        const nextLayerData: ITextLayerProps = { ...prevLayerData };

        if (!data) {
            if (prevLayerData.listStyle === '') return null;

            nextLayerData.listStyle = '';
            Reflect.deleteProperty(nextLayerData, 'listStyleMaterial');
        } else {
            const style = data.style;

            // TODO: 需要新增重复设置判断

            nextLayerData.listStyle = style;
            nextLayerData.textAlign = TextAlignType.LEFT; // 使用列表样式时，强制文本左对齐

            if (style === 'custom') {
                nextLayerData.listStyleMaterial = data.config;
            } else {
                Reflect.deleteProperty(nextLayerData, 'listStyleMaterial');
            }
        }

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        // 自定义 marker 包含自定义字体场景
        if (data && data.style === 'custom' && data.config.fontId) {
            const font = this.framework.service.font.getById(data.config.fontId);
            if (font) nextLayer.__listMarkerFontFamily = font.name;
        } else {
            Reflect.deleteProperty(nextLayer, '__listMarkerFontFamily');
        }

        nextLayer = refreshRuntimeFontFamilies(nextLayer);

        nextLayer.materialIds = {
            ...(nextLayer.materialIds || {}),
            [MaterialIdsType.TEXT_LIST]: data ? [data.id] : [],
            [MaterialIdsType.FONT_FAMILY]: collectFontMaterialIDs(this.framework.service.font, nextLayer),
        };

        if (prevLayerData.shapeExtension) return null;

        const requests = collectFontSubsetRequests(nextLayer).filter((request) => request.chars);
        const results = await Promise.all(
            requests.map(async ({ fontFamily, chars }) => {
                if (!this.framework.service.font.getByFamily(fontFamily)) return false;
                const subsetFamily = await this.framework.service.font.loadSubset(fontFamily, chars);
                if (!subsetFamily) return false;

                setLayerRuntimeValue(nextLayer, {
                    fontFamilyMap: {
                        ...(nextLayer.__runtime?.fontFamilyMap || {}),
                        [fontFamily]: subsetFamily,
                    },
                });
                return true;
            }),
        );
        if (!results.every(Boolean)) return null;

        const object = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id);
        const resolution = await object?.layoutAsync(nextLayer);
        // 列表属性和尺寸必须消费同一个 ready 候选结果。
        if (resolution?.status !== 'ready') return null;

        return applySnapshotSize(nextLayer, resolution.value);
    }

    async applyListText(layerID: string, data: ListStyleStruct) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const nextLayer = await this.prepare(prevLayer, data);
        if (!nextLayer) return prevLayer;

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, FUNC_ID);

        return nextLayer;
    }

    async cancelListText(layerID: string) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const nextLayer = await this.prepare(prevLayer, null);
        if (!nextLayer) return prevLayer;

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, FUNC_ID);

        return nextLayer;
    }

    /**
     * 更新自定义列表 marker，并用真实宿主同步新的列表缩进几何。
     *
     * @param layerID - 目标 Text Layer ID。
     * @param data - 新的自定义列表素材配置。
     * @param options - 是否结束连续修改并写入历史。
     * @returns 更新后的图层；输入或 renderer 不可用时返回 void。
     */
    updateListStyleMaterial(
        layerID: string,
        data: IListStyleMaterial,
        options = { writeHistory: false },
    ): ILayerProps | void {
        const { writeHistory = false } = options;

        const prevLayer = (() => {
            if (this.prevLayer && this.prevLayer.id === layerID) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();

        const abort = () => {
            if (writeHistory) this.prevLayer = null;
            return;
        };

        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return abort();

        const prevLayerData = prevLayer.data as ITextLayerProps;
        if (!prevLayerData.listStyleMaterial) return abort();

        const candidate: ILayerProps = { ...prevLayer, data: { ...prevLayerData, listStyleMaterial: data } };
        const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, candidate.id)?.layout(candidate);
        // 素材配置与列表缩进只能在 ready 时共同提交。
        if (resolution?.status !== 'ready') return abort();
        const snapshot = resolution.value;

        const nextLayer = applySnapshotSize(candidate, snapshot);
        requestTextSVGPreview(this.framework, writeHistory ? 'commit' : 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { activate: true, dataOnly: !writeHistory });

        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, FUNC_ID);
            this.prevLayer = null;
        }

        return nextLayer;
    }
}
